// src/components/PatternBG.js
import React, { useEffect, useRef } from "react";

/**
 * Animated background pattern that scrolls upward.
 * variant: "wavy" | "grid"
 * speed: px/sec
 * density: base spacing between lines (larger = looser)
 * stroke: CSS color with alpha (e.g., "rgba(0,0,0,0.10)")
 */
export default function PatternBG({
  variant = "wavy",
  speed = 18,
  density = 72,
  stroke = "rgba(0,0,0,0.10)",
  lineWidth = 1,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef();
  const startRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: true });

    let w, h, dpr, offsetY = 0;
    let tile, tctx, tileW, tileH;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;

      // Fill the entire Motion section height
      const section = document.querySelector(".motion-heading");
      h = section ? section.offsetHeight : window.innerHeight;

      canvas.width  = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      buildTile(); // rebuild so pattern stays crisp & consistent
    };

    const buildTile = () => {
      // Keep scale consistent relative to viewport (prevents "stretchy" look)
      const base = Math.max(48, Math.min(84, Math.round(Math.min(w, h) / 14)));
      const step  = density || base;

      tileW = Math.max(320, step * 4);   // 4 cells wide
      tileH = Math.max(320, step * 4);   // 4 cells tall

      tile = document.createElement("canvas");
      tctx = tile.getContext("2d");
      tile.width = tileW;
      tile.height = tileH;

      tctx.clearRect(0, 0, tileW, tileH);
      tctx.strokeStyle = stroke;
      tctx.lineWidth = lineWidth;
      tctx.lineCap = "round";

      if (variant === "grid") drawGrid(step);
      else drawWavy(step); // default
    };

    const drawGrid = (step) => {
      for (let x = 0; x <= tileW; x += step) {
        tctx.beginPath();
        tctx.moveTo(x + 0.5, 0);
        tctx.lineTo(x + 0.5, tileH);
        tctx.stroke();
      }
      for (let y = 0; y <= tileH; y += step) {
        tctx.beginPath();
        tctx.moveTo(0, y + 0.5);
        tctx.lineTo(tileW, y + 0.5);
        tctx.stroke();
      }
    };

    // ✅ Seamless, non-stretched waves (normalized to tile size)
    const drawWavy = (step) => {
      const cols = Math.round(tileW / step);
      const rows = Math.round(tileH / step);
      const amp  = step * 0.32;

      // vertical wavy lines
      for (let i = 0; i <= cols; i++) {
        const x0 = i * step;
        tctx.beginPath();
        for (let y = 0; y <= tileH; y += 1.5) {
          const phase = (i % 2 === 0 ? 0 : Math.PI / 2);
          // normalize y to [0..2π] using tileH
          const x = x0 + Math.sin((y / tileH) * Math.PI * 2 + phase) * amp * 0.35;
          if (y === 0) tctx.moveTo(x, y);
          else tctx.lineTo(x, y);
        }
        tctx.stroke();
      }

      // horizontal wavy lines
      for (let j = 0; j <= rows; j++) {
        const y0 = j * step;
        tctx.beginPath();
        for (let x = 0; x <= tileW; x += 1.5) {
          const phase = (j % 2 === 0 ? 0 : Math.PI / 2);
          // normalize x to [0..2π] using tileW
          const y = y0 + Math.sin((x / tileW) * Math.PI * 2 + phase) * amp * 0.35;
          if (x === 0) tctx.moveTo(x, y);
          else tctx.lineTo(x, y);
        }
        tctx.stroke();
      }
    };

    const frame = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const dt = (ts - startRef.current) / 1000;
      startRef.current = ts;

      offsetY = (offsetY - speed * dt) % tileH;

      ctx.clearRect(0, 0, w, h);
      const yStart = Math.floor(offsetY);

      // tile across width AND height (no scaling)
      for (let x = 0; x < w + tileW; x += tileW) {
        for (let y = yStart; y < h + tileH; y += tileH) {
          ctx.drawImage(tile, x, y);
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    resize();
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!reduced) rafRef.current = requestAnimationFrame(frame);
    else {
      ctx.clearRect(0, 0, w, h);
      buildTile();
      for (let x = 0; x < w + tileW; x += tileW) ctx.drawImage(tile, x, 0);
    }

    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [variant, speed, density, stroke, lineWidth]);

  return <canvas className="pattern-canvas" ref={canvasRef} />;
}